import { create } from 'zustand';
import { fetchScheduledClasses as fetchScheduledClassesFromApi } from '../schedule/useScheduledClasses';
import { getUserGymId } from '@/utils/gymAuth';
import { classesService } from '@/services/classesService';
import { scheduleClassesService } from '@/services/scheduleClassesService';
import { useUserStore } from '@/store/userStore';
interface ScheduleSupabaseState {
  scheduledClasses: any[];
  isLoading: boolean;
  error: string | null;
  fetchScheduledClasses: (startDate?: string, endDate?: string) => Promise<void>;
  updateScheduledClass: (classId: string, updateData: any) => Promise<boolean>;
  addScheduledClass: (classData: any) => Promise<boolean>;
}

export const useScheduleSupabaseStore = create<ScheduleSupabaseState>((set, get) => ({
  scheduledClasses: [],
  isLoading: false,
  error: null,
  
  fetchScheduledClasses: async (startDate?: string, endDate?: string) => {
    set({ isLoading: true, error: null });
    try {
      console.log(`Fetching scheduled classes for dates: ${startDate || 'all'} to ${endDate || 'all'}`);
      
      // Pass date parameters to the fetch function
      const classes = await fetchScheduledClassesFromApi(startDate, endDate);
      
      // Filter by gym_id
      const filteredClasses = classes.filter(cls => {
        // Assuming class_availability view includes gym_id or we need to check it
        // For now, we'll rely on RLS policies to handle this
        return true;
      });
      
      set({ 
        scheduledClasses: filteredClasses,
        isLoading: false
      });
      
      console.log(`Successfully fetched ${filteredClasses.length} scheduled classes`);
    } catch (error: any) {
      console.error('Error in fetchScheduledClasses store method:', error);
      set({ 
        error: error.message || 'Failed to fetch scheduled classes',
        isLoading: false 
      });
    }
  },
  
  // Add method to update a scheduled class
  updateScheduledClass: async (classId: string, updateData: any) => {
    set({ isLoading: true, error: null });
    
    const gymId = getUserGymId();
    if (!gymId) {
      set({ 
        error: 'Gym not selected',
        isLoading: false 
      });
      return false;
    }
    
    try {
      const success = await classesService.updateScheduledClass(gymId, classId, updateData);
      
      if (!success) {
        throw new Error('Failed to update class');
      }
      
      const { user } = useUserStore.getState();
      if (user?.id) {
        scheduleClassesService.invalidateCache(user.id, gymId);
        console.log('[addScheduledClass] Cache invalidated after class creation');
      }
      
      // Refresh the classes list after update
      const { startDate, endDate } = updateData.class_date ? 
        { startDate: updateData.class_date, endDate: updateData.class_date } : 
        { startDate: undefined, endDate: undefined };
      
      // Appeler la méthode du store, pas le helper importé : ce dernier
      // rechargeait depuis le réseau puis jetait le résultat, laissant
      // `scheduledClasses` sur les données d'avant la mise à jour.
      await get().fetchScheduledClasses(startDate, endDate);

      set({ isLoading: false });
      return true;
    } catch (error: any) {
      console.error('Error updating scheduled class:', error);
      set({ 
        error: error.message || 'Failed to update class',
        isLoading: false 
      });
      return false;
    }
  },
  
  // Add method to add a new scheduled class
  addScheduledClass: async (classData: any) => {
    set({ isLoading: true, error: null });
    
    const gymId = getUserGymId();
    if (!gymId) {
      set({ 
        error: 'Gym not selected',
        isLoading: false 
      });
      return false;
    }
    
    try {
      const success = await classesService.addScheduledClass(gymId, classData);
      
      if (!success) {
        throw new Error('Failed to add class');
      }
      
      // Invalider le cache du service pour forcer un refresh
      const { user } = useUserStore.getState();
      if (user?.id) {
        scheduleClassesService.invalidateCache(user.id, gymId);
        console.log('[addScheduledClass] Cache invalidated after class creation');
      }
      
      // Idem : passer par la méthode du store pour que la liste soit réellement
      // remise à jour dans l'état.
      await get().fetchScheduledClasses(classData.class_date, classData.class_date);
      
      set({ isLoading: false });
      return true;
    } catch (error: any) {
      console.error('Error adding scheduled class:', error);
      set({ 
        error: error.message || 'Failed to add class',
        isLoading: false 
      });
      return false;
    }
  }
}));
